import type { LibraryPieceRef } from '@/lib/api/generated/model';

import type { BundleAnswersRequest } from './upload/bundleAnswers';

/**
 * Cache identity for the workflows feature.
 *
 * The feature's lists and definitions come from the workspace aggregate (spec
 * D-10) and have no key of their own. The one read it makes itself is the
 * builder's: a published workflow with its pending changes (spec 144). The
 * export dialog reads what a workflow's bundle would hold (spec 203).
 *
 * A bundle import's check is keyed by the file and the answers it was made with
 * (spec 208): the check runs by itself as answers change, and only the response
 * for the current answers may be shown, never a slower one for answers since
 * changed. The file's id comes from `useBundleCheck`, since a `File` cannot be
 * part of a key.
 */
export const workflowKeys = {
  editing: (workspaceId: string, workflowId: string) =>
    ['workflows', 'editing', workspaceId, workflowId] as const,
  bundlePreview: (workspaceId: string, workflowId: string) =>
    ['workflows', 'bundle-preview', workspaceId, workflowId] as const,
  sharePreview: (
    workspaceId: string,
    workflowId: string,
    agents: readonly string[],
    skills: readonly string[],
    useLibrary: readonly LibraryPieceRef[] = [],
  ) =>
    [
      'workflows',
      'share-preview',
      workspaceId,
      workflowId,
      agents,
      skills,
      useLibrary,
    ] as const,
  bundleCheck: (
    workspaceId: string,
    fileId: number,
    answers: BundleAnswersRequest,
  ) => ['workflows', 'bundle-check', workspaceId, fileId, answers] as const,
  sourceBundle: (
    workspaceId: string,
    workflowId: string,
    sent: { agents: readonly string[]; skills: readonly string[] },
  ) =>
    [
      'workflows',
      'source-bundle',
      workspaceId,
      workflowId,
      sent.agents,
      sent.skills,
    ] as const,
};
